import React, { useState, useRef } from 'react';
import { buildApiUrl } from '../../config/api.config';

interface UploadResult {
  success: boolean;
  reportId?: number;
  message: string;
  data?: any;
}

const CSVReportGenerator: React.FC = () => {
  const [salesFile, setSalesFile] = useState<File | null>(null);
  const [adSpendFile, setAdSpendFile] = useState<File | null>(null);
  const [reportType, setReportType] = useState<'combined' | 'sales' | 'adspend'>('combined');
  const [reportName, setReportName] = useState('');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [calculationType, setCalculationType] = useState<'standard' | 'cody'>('standard');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [salesDragActive, setSalesDragActive] = useState(false);
  const [adSpendDragActive, setAdSpendDragActive] = useState(false);
  
  const salesFileInputRef = useRef<HTMLInputElement>(null);
  const adSpendFileInputRef = useRef<HTMLInputElement>(null);

  const handleSalesDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setSalesDragActive(true);
    } else if (e.type === "dragleave") {
      setSalesDragActive(false);
    }
  };

  const handleAdSpendDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setAdSpendDragActive(true);
    } else if (e.type === "dragleave") {
      setAdSpendDragActive(false);
    }
  };

  const handleSalesDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSalesDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setSalesFile(file);
        if (!reportName) {
          setReportName(file.name.replace('.csv', '').replace(/sales?/gi, '').trim());
        }
      } else {
        alert('Please select a CSV file');
      }
    }
  };

  const handleAdSpendDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setAdSpendDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setAdSpendFile(file);
      } else {
        alert('Please select a CSV file');
      }
    }
  };

  const handleSalesFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSalesFile(file);
      if (!reportName) {
        setReportName(file.name.replace('.csv', '').replace(/sales?/gi, '').trim());
      }
    }
  };

  const handleAdSpendFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setAdSpendFile(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation based on report type
    if (reportType === 'combined') {
      if (!salesFile || !adSpendFile) {
        alert('Please select both Sales CSV and Ad Spend CSV files for a combined report');
        return;
      }
    } else if (reportType === 'sales') {
      if (!salesFile) {
        alert('Please select a Sales CSV file');
        return;
      }
    } else if (reportType === 'adspend') {
      if (!adSpendFile) {
        alert('Please select an Ad Spend CSV file');
        return;
      }
    }

    if (!reportName.trim()) {
      alert('Please enter a report name');
      return;
    }

    setIsUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      
      if (reportType === 'combined') {
        // For combined reports, use a new endpoint that will be created
        formData.append('sales_file', salesFile!);
        formData.append('adspend_file', adSpendFile!);
        formData.append('report_type', 'combined');
      } else if (reportType === 'sales') {
        formData.append('file', salesFile!);
        formData.append('report_type', 'sales');
      } else {
        formData.append('file', adSpendFile!);
        formData.append('report_type', 'adspend');
      }
      
      formData.append('report_name', reportName);
      formData.append('month', month.toString());
      formData.append('year', year.toString());
      formData.append('calculation_type', calculationType);

      const token = localStorage.getItem('authToken');
      
      // Use different endpoint for combined reports
      const endpoint = reportType === 'combined' 
        ? '/v2/reports/direct/upload-combined' 
        : '/v2/reports/direct/upload';
      
      const response = await fetch(buildApiUrl(endpoint), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        setUploadResult({
          success: true,
          reportId: result.report_id,
          message: 'Report generated successfully!',
          data: result.data
        });
        
        // Clear form
        setSalesFile(null);
        setAdSpendFile(null);
        setReportName('');
        if (salesFileInputRef.current) {
          salesFileInputRef.current.value = '';
        }
        if (adSpendFileInputRef.current) {
          adSpendFileInputRef.current.value = '';
        }
      } else {
        setUploadResult({
          success: false,
          message: result.message || 'Upload failed'
        });
      }
    } catch (error) {
      setUploadResult({
        success: false,
        message: `Upload error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setSalesFile(null);
    setAdSpendFile(null);
    setReportName('');
    setUploadResult(null);
    if (salesFileInputRef.current) {
      salesFileInputRef.current.value = '';
    }
    if (adSpendFileInputRef.current) {
      adSpendFileInputRef.current.value = '';
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Generate Report from CSV</h2>
      
      {/* Workflow Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <div className="text-blue-600 mr-3 mt-0.5">💡</div>
          <div>
            <h3 className="font-medium text-blue-900 mb-1">Report Generation Options</h3>
            <p className="text-sm text-blue-700 mb-2">
              Choose how you want to generate your marketing report:
            </p>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• <strong>Combined Report</strong>: Upload both sales and ad spend CSV files for complete profit analysis</li>
              <li>• <strong>Sales Only</strong>: Upload sales data to see revenue and basic profit metrics</li>
              <li>• <strong>Ad Spend Only</strong>: Upload marketing costs data to track campaign spending</li>
            </ul>
          </div>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* File Upload Areas */}
        <div className="space-y-4">
          {/* Combined Report - Two File Upload Areas */}
          {reportType === 'combined' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Sales CSV Upload */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Sales CSV File *
                </label>
                <div
                  className={`relative border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    salesDragActive
                      ? 'border-blue-400 bg-blue-50'
                      : salesFile
                      ? 'border-green-400 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleSalesDrag}
                  onDragLeave={handleSalesDrag}
                  onDragOver={handleSalesDrag}
                  onDrop={handleSalesDrop}
                >
                  <input
                    ref={salesFileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleSalesFileSelect}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  {salesFile ? (
                    <div className="text-green-600">
                      <div className="text-sm font-medium">✓ {salesFile.name}</div>
                      <div className="text-xs">({(salesFile.size / 1024).toFixed(1)} KB)</div>
                    </div>
                  ) : (
                    <div className="text-gray-500">
                      <div className="text-sm font-medium">Sales Data</div>
                      <div className="text-xs">Jobs, revenue, costs</div>
                      <div className="text-xs mt-1">Drop or click to browse</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Ad Spend CSV Upload */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Ad Spend CSV File *
                </label>
                <div
                  className={`relative border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    adSpendDragActive
                      ? 'border-blue-400 bg-blue-50'
                      : adSpendFile
                      ? 'border-green-400 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleAdSpendDrag}
                  onDragLeave={handleAdSpendDrag}
                  onDragOver={handleAdSpendDrag}
                  onDrop={handleAdSpendDrop}
                >
                  <input
                    ref={adSpendFileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleAdSpendFileSelect}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  {adSpendFile ? (
                    <div className="text-green-600">
                      <div className="text-sm font-medium">✓ {adSpendFile.name}</div>
                      <div className="text-xs">({(adSpendFile.size / 1024).toFixed(1)} KB)</div>
                    </div>
                  ) : (
                    <div className="text-gray-500">
                      <div className="text-sm font-medium">Ad Spend Data</div>
                      <div className="text-xs">Marketing costs by source</div>
                      <div className="text-xs mt-1">Drop or click to browse</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Sales Only - Single File Upload */}
          {reportType === 'sales' && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Sales CSV File *
              </label>
              <div
                className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  salesDragActive
                    ? 'border-blue-400 bg-blue-50'
                    : salesFile
                    ? 'border-green-400 bg-green-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleSalesDrag}
                onDragLeave={handleSalesDrag}
                onDragOver={handleSalesDrag}
                onDrop={handleSalesDrop}
              >
                <input
                  ref={salesFileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleSalesFileSelect}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                {salesFile ? (
                  <div className="text-green-600">
                    <div className="text-lg font-medium">✓ {salesFile.name}</div>
                    <div className="text-sm">({(salesFile.size / 1024).toFixed(1)} KB)</div>
                  </div>
                ) : (
                  <div className="text-gray-500">
                    <div className="text-lg font-medium">Drop Sales CSV file here or click to browse</div>
                    <div className="text-sm">Jobs, revenue, costs, lead sources</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Ad Spend Only - Single File Upload */}
          {reportType === 'adspend' && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Ad Spend CSV File *
              </label>
              <div
                className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  adSpendDragActive
                    ? 'border-blue-400 bg-blue-50'
                    : adSpendFile
                    ? 'border-green-400 bg-green-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleAdSpendDrag}
                onDragLeave={handleAdSpendDrag}
                onDragOver={handleAdSpendDrag}
                onDrop={handleAdSpendDrop}
              >
                <input
                  ref={adSpendFileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleAdSpendFileSelect}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                {adSpendFile ? (
                  <div className="text-green-600">
                    <div className="text-lg font-medium">✓ {adSpendFile.name}</div>
                    <div className="text-sm">({(adSpendFile.size / 1024).toFixed(1)} KB)</div>
                  </div>
                ) : (
                  <div className="text-gray-500">
                    <div className="text-lg font-medium">Drop Ad Spend CSV file here or click to browse</div>
                    <div className="text-sm">Marketing costs by source</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Report Configuration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Report Name
            </label>
            <input
              type="text"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter report name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Report Type
            </label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value as 'combined' | 'sales' | 'adspend')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="combined">Combined Report (Sales + Ad Spend)</option>
              <option value="sales">Sales Data Only</option>
              <option value="adspend">Ad Spend Data Only</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Month
            </label>
            <select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(2024, i).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Year
            </label>
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              min="2020"
              max="2030"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Calculation Type (for sales and combined reports) */}
        {(reportType === 'sales' || reportType === 'combined') && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Calculation Type
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="standard"
                  checked={calculationType === 'standard'}
                  onChange={(e) => setCalculationType(e.target.value as 'standard' | 'cody')}
                  className="mr-2"
                />
                Standard
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="cody"
                  checked={calculationType === 'cody'}
                  onChange={(e) => setCalculationType(e.target.value as 'standard' | 'cody')}
                  className="mr-2"
                />
                Cody Cline
              </label>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-4">
          <button
            type="submit"
            disabled={
              isUploading || 
              (reportType === 'combined' && (!salesFile || !adSpendFile)) ||
              (reportType === 'sales' && !salesFile) ||
              (reportType === 'adspend' && !adSpendFile)
            }
            className={`flex-1 py-2 px-4 rounded-md font-medium ${
              isUploading || 
              (reportType === 'combined' && (!salesFile || !adSpendFile)) ||
              (reportType === 'sales' && !salesFile) ||
              (reportType === 'adspend' && !adSpendFile)
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isUploading ? 'Processing...' : 'Generate Report'}
          </button>
          
          <button
            type="button"
            onClick={resetForm}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Reset
          </button>
        </div>
      </form>

      {/* Upload Result */}
      {uploadResult && (
        <div className={`mt-6 p-4 rounded-md ${
          uploadResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          <div className={`font-medium ${uploadResult.success ? 'text-green-800' : 'text-red-800'}`}>
            {uploadResult.success ? '✓ Success!' : '✗ Error'}
          </div>
          <div className={`text-sm mt-1 ${uploadResult.success ? 'text-green-700' : 'text-red-700'}`}>
            {uploadResult.message}
          </div>
          {uploadResult.success && uploadResult.reportId && (
            <div className="mt-2">
              <button
                onClick={() => window.open(`/reports/generated/${uploadResult.reportId}`, '_blank')}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                View Generated Report →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CSVReportGenerator;